// @flow
import React from 'react'
import DatetimePicker from 'react-datetime'
import classNames from 'classnames'
import I18n from 'i18n-react'
import ScrollLock from 'react-scrolllock'
import isMobile from 'is-mobile'

import DateText from './DateText'

// core
import moment from '../../Moment'
import { renderDate } from '../../DateTimeFunctions'

// types
import type { DefaultFormFieldState, DefaultFormFieldType } from './AbstractFormField'

type DateTimeInputsProps = DefaultFormFieldType & {
  domain: {
    _type: string,
    pattern?: string,
    minDate?: string,
    maxDate?: string,
  },
  hideButton?: boolean,
  value?: Date,
  doNotRenderDatePicker?: boolean,
  relative?: boolean | string,
  className?: string,
}

type DateTimeInputsState = DefaultFormFieldState & {
  editCompleted: boolean,
  pickerOpened: boolean,
  day: string,
  month: string,
  year: string,
}

export default class DateInputs extends DateText<DateTimeInputsProps, DateTimeInputsState> {
  constructor(props: DateTimeInputsProps) {
    super(props)
    this.state = {
      editCompleted: true,
      pristine: true,
      day: '',
      month: '',
      year: '',
      pickerOpened: false,
      prevPropsValue: '',
      placeholderDay: I18n.translate('placeholder.day'),
    }
    this.monthRef = React.createRef()
    this.yearRef = React.createRef()
  }

  componentDidMount() {
    if (this.props.value) {
      if (!this.props.textMode) {
        this.checkDate(this.props.value)
      }
      this.setLocalInputsFromValue(this.props)
    }
    if (!this.props.textMode) {
      let relativeMinDate = ''
      if (typeof this.props.relative === 'string' && this.props.formData && this.props.formData[this.props.relative]) {
        relativeMinDate = moment(this.props.formData[this.props.relative]).add(-1, 'day')
      }
      this.changeRange(this.props, relativeMinDate)
    }
  }

  componentDidUpdate(prevProps: DateTimeInputsProps) {
    if (this.props.value !== prevProps.value) {
      this.checkDate(this.props.value)
      this.setLocalInputsFromValue(this.props)
    }
    if (
      (!this.props.textMode && JSON.stringify(this.props.domain) !== JSON.stringify(prevProps.domain)) ||
      (this.props.relative && this.props.formData[this.props.relative] !== prevProps.formData[this.props.relative])
    ) {
      let relativeMinDate = ''
      if (typeof this.props.relative === 'string' && this.props.formData && this.props.formData[this.props.relative]) {
        relativeMinDate = moment(this.props.formData[this.props.relative]).add(-1, 'day')
      }
      this.changeRange(this.props, relativeMinDate)
    }
    if (this.state.placeholderDay !== I18n.translate('placeholder.day')) {
      this.setState({ placeholderDay: I18n.translate('placeholder.day') })
    }
  }

  setLocalInputsFromValue = (props: DateTimeInputsProps) => {
    const date = moment(props.value, ['YYYY-MM-DD', 'DD.MM.YYYY'])
    if (date.isValid()) {
      this.setState({
        day: date.format('DD'),
        month: date.format('MM'),
        year: date.format('YYYY'),
      })
    } else if (!props.value) {
      this.setState({
        day: '',
        month: '',
        year: '',
      })
    }
  }

  handleTogglePicker = () => {
    this.setState({ pickerOpened: !this.state.pickerOpened })
  }

  handleOnChange = (datetime: string | Object) => {
    if (!datetime || typeof datetime === 'string') {
      this.setState({ value: '', pickerOpened: false, day: '', month: '', year: '' })
    } else {
      this.setState({ value: datetime, pickerOpened: false })
    }
  }

  handleOnBlur = (datetime: string | Object) => {
    let value = ''
    if (!datetime || typeof datetime === 'string') {
      this.props.onChange('')
    } else {
      this.setState({ value: datetime, pickerOpened: false, pristine: false })
      value = datetime.format('YYYY-MM-DD')
      this.props.onChange(value)
    }
    if (this.props.onBlur) {
      this.props.onBlur(`${this.props.prefix}${this.props.name}`, value, [], value !== this.props.value)
    }
  }

  handleOnChangeInput = (event: SyntheticInputEvent<HTMLInputElement>, key: string) => {
    if (key === 'day' && `${event.target.value}`.length <= 2) {
      this.setState({ [key]: event.target.value })
      if (`${event.target.value}`.length === 2 && moment(event.target.value, 'DD').isValid()) {
        this.handleOnInputBlur(null, key, event.target.value, true)
      }
    } else if (key === 'month' && `${event.target.value}`.length <= 2) {
      this.setState({ [key]: event.target.value })
      if (`${event.target.value}`.length === 2 && moment(event.target.value, 'MM').isValid()) {
        this.handleOnInputBlur(null, key, event.target.value, true)
      }
    } else if (key === 'year' && `${event.target.value}`.length <= 4) {
      this.setState({ [key]: event.target.value })
      if (`${event.target.value}`.length === 4 && moment(event.target.value, 'YYYY').isValid()) {
        this.handleOnInputBlur(null, key, event.target.value, true)
      }
    }
  }

  handleOnInputBlur = (
    event: null | SyntheticInputEvent<HTMLInputElement>,
    key: string,
    value?: string,
    focusRef?: boolean,
  ) => {
    let inputValue = value || ''
    if (!value && event && event.target) {
      inputValue = event.target.value
    }
    let newState = { [key]: inputValue }
    if (key === 'year') {
      // $FlowIssue
      newState.pristine = false
    }
    this.setState(newState)
    const values = ['day', 'month', 'year'].map((k) => (k === key ? inputValue : this.state[k])).filter((val) => !!val)
    if (values.length === 3) {
      const newDate = `${values[2]}-${values[1]}-${values[0]}`
      if (moment(newDate, 'YYYY-MM-DD').isValid()) {
        this.handleOnBlur(moment(newDate, 'YYYY-MM-DD'))
      } else {
        this.props.onChange(newDate)
      }
    }
    /* if (focusRef) {
      if (key === 'day') {
        this.monthRef.current.focus()
      } else if (key === 'month') {
        this.yearRef.current.focus()
      }
    } */
  }

  render() {
    const {
      textMode,
      value,
      id,
      name,
      enabled,
      hideButton,
      doNotRenderDatePicker,
      relative,
      formData,
      className,
    } = this.props
    const { day, month, year, editCompleted, pickerOpened } = this.state

    if (textMode) {
      return <span>{renderDate(value, editCompleted)}</span>
    }

    let relativeMomentObject = ''
    if (typeof relative === 'string' && formData && formData[relative]) {
      relativeMomentObject = moment(formData[relative])
    }

    let valueMomentObject = ''
    if (value) {
      valueMomentObject = moment(value)
    }

    if (relativeMomentObject && (!valueMomentObject || relativeMomentObject.diff(valueMomentObject) > 0)) {
      valueMomentObject = relativeMomentObject
    }

    const classes = ['row ml-0 p-0']
    const wrapperClasses = ['react-datepicker  datepicker-button']
    if (this.hasErrors() && !this.isPristine()) {
      classes.push('is-invalid')
      wrapperClasses.push('is-invalid')
    }
    const contentWrapper = [
      'd-flex align-items-center',
      {
        'without-button': hideButton,
        [className]: !!className,
      },
    ]

    return (
      <div className={classNames(contentWrapper)}>
        <div className={classNames(classes)}>
          <div className="col-3 pl-0">
            <div className="small-inputs">
              <input
                className="form-control"
                data-hj-whitelist
                id={`${id}-day-input`}
                max="31"
                min="1"
                name={`${name}-day`}
                onBlur={(event) => {
                  this.handleOnInputBlur(event, 'day')
                }}
                onChange={(event) => {
                  this.handleOnChangeInput(event, 'day')
                }}
                placeholder={I18n.translate('placeholder.day')}
                readOnly={!enabled}
                type="number"
                value={day}
              />
            </div>
          </div>
          <div className="col-3 pl-0">
            <div className="small-inputs">
              <input
                className="form-control"
                data-hj-whitelist
                id={`${id}-month-input`}
                max="12"
                min="1"
                name={`${name}-month`}
                onBlur={(event) => {
                  this.handleOnInputBlur(event, 'month')
                }}
                onChange={(event) => {
                  this.handleOnChangeInput(event, 'month')
                }}
                placeholder={I18n.translate('placeholder.month')}
                readOnly={!enabled}
                ref={this.monthRef}
                type="number"
                value={month}
              />
            </div>
          </div>
          <div className="col-3 pl-0">
            <div className="small-inputs">
              <input
                className="form-control"
                data-hj-whitelist
                id={`${id}-year-input`}
                min="1900"
                name={`${name}-year`}
                onBlur={(event) => {
                  this.handleOnInputBlur(event, 'year')
                }}
                onChange={(event) => {
                  this.handleOnChangeInput(event, 'year')
                }}
                placeholder={I18n.translate('placeholder.year')}
                readOnly={!enabled}
                ref={this.yearRef}
                type="number"
                value={year}
              />
            </div>
          </div>
          {enabled && !hideButton && (
            <div className="col-3 pr-0 text-right">
              <button className="btn btn-only-icon btn-dark" onClick={this.handleTogglePicker} type="button">
                <i className="pfp pfp-calendar" />
              </button>
            </div>
          )}
          {this.renderError()}
        </div>
        {!doNotRenderDatePicker && [
          <div className={classNames(wrapperClasses)} key="datePicker">
            <DatetimePicker
              closeOnSelect
              input={false}
              isValidDate={this.handleIsValidDate}
              locale="cs-CZ"
              onBlur={this.handleOnBlur}
              onChange={this.handleOnChange}
              open={pickerOpened}
              strictParsing
              timeFormat={false}
              value={this.state.value}
              viewDate={valueMomentObject ? valueMomentObject : this.state.maxDate ? this.state.maxDate : undefined}
              viewMode={valueMomentObject ? 'days' : 'years'}
            />
          </div>,
          pickerOpened && isMobile() && (
            <div
              className="modal-backdrop fade show"
              key="backdrop"
              onClick={this.handleTogglePicker}
              style={{ zIndex: 5 }}
            />
          ),
        ]}
        {pickerOpened && (
          <div
            onClick={this.handleTogglePicker}
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh' }}
          />
        )}
        {pickerOpened && isMobile() && <ScrollLock />}
      </div>
    )
  }
}
